import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";

export interface DeletePersonaScriptsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedScriptCount: number;
  selectedBriefCount?: number;
  onConfirm: () => void;
  isDeleting?: boolean;
}

export function DeletePersonaScriptsDialog({
  open,
  onOpenChange,
  selectedScriptCount,
  selectedBriefCount = 0,
  onConfirm,
  isDeleting,
}: DeletePersonaScriptsDialogProps) {
  const { t } = useTranslation();
  const scriptsLabel = t("personaOpportunities.scripts.summaryScripts", {
    defaultValue_one: "{{count}} script",
    defaultValue_other: "{{count}} scripts",
    count: selectedScriptCount,
  });
  const briefsLabel = t("personaOpportunities.scripts.summaryBriefs", {
    defaultValue_one: "{{count}} brief",
    defaultValue_other: "{{count}} briefs",
    count: selectedBriefCount,
  });
  const isMixed = selectedScriptCount > 0 && selectedBriefCount > 0;
  const isBriefsOnly = selectedBriefCount > 0 && selectedScriptCount === 0;
  const confirmCount = isBriefsOnly ? selectedBriefCount : selectedScriptCount;

  const title = isMixed
    ? t("personaOpportunities.scripts.deleteDialog.titleMixed", {
        defaultValue: "Delete {{scripts}} and {{briefs}}?",
        scripts: scriptsLabel,
        briefs: briefsLabel,
      })
    : isBriefsOnly
      ? t("personaOpportunities.scripts.briefs.deleteDialog.title", {
          count: selectedBriefCount,
          defaultValue_one: "Delete {{count}} brief?",
          defaultValue_other: "Delete {{count}} briefs?",
        })
      : t("personaOpportunities.scripts.deleteDialog.title", {
          count: selectedScriptCount,
          defaultValue_one: "Delete {{count}} script?",
          defaultValue_other: "Delete {{count}} scripts?",
        });

  const description = isMixed
    ? t(
        "personaOpportunities.scripts.deleteDialog.descriptionMixed",
        "This cannot be undone. Generation is expensive. Hook variants of a selected parent script will also be removed.",
      )
    : isBriefsOnly
    ? t(
        "personaOpportunities.scripts.briefs.deleteDialog.description",
        "This cannot be undone. Generation is expensive.",
      )
    : t(
        "personaOpportunities.scripts.deleteDialog.description",
        "This cannot be undone. Generation is expensive. Hook variants of a selected parent will also be removed.",
      );

  const confirmLabel = isMixed
    ? t("personaOpportunities.scripts.deleteDialog.confirmMixed", {
        defaultValue: "Delete {{scripts}} and {{briefs}}",
        scripts: scriptsLabel,
        briefs: briefsLabel,
      })
    : isBriefsOnly
      ? t("personaOpportunities.scripts.briefs.deleteDialog.confirm", {
          count: selectedBriefCount,
          defaultValue_one: "Delete {{count}} brief",
          defaultValue_other: "Delete {{count}} briefs",
        })
      : t("personaOpportunities.scripts.deleteDialog.confirm", {
          count: confirmCount,
          defaultValue_one: "Delete {{count}} script",
          defaultValue_other: "Delete {{count}} scripts",
        });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
          >
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirm}
            disabled={isDeleting}
            data-testid="persona-script-delete-confirm"
          >
            {isDeleting
              ? t("personaOpportunities.scripts.deleteDialog.deleting", "Deleting…")
              : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
